import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface ProtocolosFilters {
  numeroProtocolo: string;
  uidProfesional: string;
  uidEstablecimiento: string;
  sortOrder: 'asc' | 'desc';
}

export function useProtocolosFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    if (searchParams.has('sort_order')) return;

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('sort_order', 'desc');
      return next;
    }, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filters: ProtocolosFilters = useMemo(() => {
    return {
      numeroProtocolo: searchParams.get('numero_protocolo') ?? '',
      uidProfesional: searchParams.get('profesional') ?? '',
      uidEstablecimiento: searchParams.get('establecimiento') ?? '',
      sortOrder: searchParams.get('sort_order') === 'asc' ? 'asc' : 'desc',
    };
  }, [searchParams]);

  const updateFilters = useCallback(
    (partial: Partial<ProtocolosFilters>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        const apply = (key: string, value: string | null | undefined) => {
          if (value === null || value === undefined || value === '') {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        };

        if ('numeroProtocolo' in partial) apply('numero_protocolo', partial.numeroProtocolo);
        if ('uidProfesional' in partial) apply('profesional', partial.uidProfesional);
        if ('uidEstablecimiento' in partial) apply('establecimiento', partial.uidEstablecimiento);
        if ('sortOrder' in partial) apply('sort_order', partial.sortOrder);

        return next;
      });
    },
    [setSearchParams]
  );

  const clearFilters = useCallback(() => {
    const next = new URLSearchParams();
    next.set('sort_order', 'desc');
    setSearchParams(next);
  }, [setSearchParams]);

  return { filters, updateFilters, clearFilters };
}
